import { useEffect, useState } from 'react';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import { attendanceConfig, attendanceMonth, createAttProposal, attInbox, decideAttProposal, getHolidays, setHolidays, importAttendance, getEmpMap, setEmpMap, purgeAttendanceMonth, attLockStatus, lockAttMonth, unlockAttMonth } from '../api/attendance';
import { parseAttendanceXlsx } from '../utils/parseAttendanceXlsx';
import AttendanceMatrix from './AttendanceMatrix';
import FaceAttendanceModal from '../components/FaceAttendanceModal';
import OnlineAttAdminModal from '../components/OnlineAttAdminModal';

const WD = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const colOf = (wd) => (wd === 0 ? 6 : wd - 1); // weekday 0=CN..6=T7 -> cột 0..6 (T2..CN)
// Màu theo trạng thái hiệu lực.
const ST = {
  work: { bg: 'color-mix(in srgb, var(--ok) 13%, transparent)', bd: 'color-mix(in srgb, var(--ok) 40%, transparent)', tx: 'var(--ok)' },
  warn: { bg: 'color-mix(in srgb, var(--warn-fill) 16%, transparent)', bd: 'color-mix(in srgb, var(--warn-fill) 45%, transparent)', tx: 'var(--warn)' },
  absent: { bg: 'color-mix(in srgb, var(--danger) 11%, transparent)', bd: 'color-mix(in srgb, var(--danger) 38%, transparent)', tx: 'var(--danger)' },
  half: { bg: 'color-mix(in srgb, var(--ok) 13%, transparent)', bd: 'color-mix(in srgb, var(--ok) 40%, transparent)', tx: 'var(--ok)' },
  off: { bg: 'var(--panel2)', bd: 'var(--border)', tx: 'var(--muted2)' },
};
// Ô nửa công (1/2 công): tô xanh 1 nửa, gạch chéo ở giữa.
const HALF_BG = 'linear-gradient(135deg, color-mix(in srgb, var(--ok) 26%, transparent) 0 calc(50% - 1px), var(--ok) calc(50% - 1px) calc(50% + 1px), transparent calc(50% + 1px) 100%)';
const labelOf = (d) => {
  if (d.status === 'off') return d.weekday === 0 ? 'CN' : 'Nghỉ (ĐK)';
  if (d.status === 'absent') return 'Nghỉ';
  if (d.status === 'half') return `${d.checkIn}–${d.checkOut} · ½`;
  if (d.checkCount >= 2) return `${d.checkIn}–${d.checkOut}`;
  if (d.checkCount === 1) return `${d.checkIn || d.checkOut} · 1 lần`;
  return '';
};

function ProposalModal({ types, date, onClose, onSubmit }) {
  const [type, setType] = useState(types[0]?.key || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const d = new Date(`${date}T00:00:00`);
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>📝 Đề xuất / giải trình — {String(d.getDate()).padStart(2, '0')}/{String(d.getMonth() + 1).padStart(2, '0')}/{d.getFullYear()}</h3>
      <div className="field">
        <label>Hình thức giải trình</label>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      <div className="field">
        <label>Lý do giải trình</label>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Nhập lý do…" />
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <button className="btn" disabled={busy} onClick={async () => { setBusy(true); try { await onSubmit(type, reason); } finally { setBusy(false); } }}>
          {busy ? 'Đang gửi…' : 'Đề xuất'}
        </button>
      </div>
    </div>
  );
}

// Modal chọn 1 THÁNG để up cho cả đợt (đơn giản nhất, khỏi lệ thuộc đoán tháng từ file).
function ImportConfirm({ files, defaultMonth, onClose, onConfirm }) {
  const [month, setMonth] = useState(defaultMonth || files.find((f) => f.month)?.month || '');
  const valid = /^\d{4}-\d{2}$/.test(month);
  const label = (m) => (/^\d{4}-\d{2}$/.test(m) ? `tháng ${m.slice(5, 7)}/${m.slice(0, 4)}` : '—');
  const diff = files.some((f) => f.month && f.month !== month);
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>⭱ Cập nhật bảng chấm công</h3>
      <div className="field" style={{ marginBottom: 8 }}>
        <label>Up cho tháng</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }} onClick={(e) => e.currentTarget.showPicker?.()} />
      </div>
      <div className="small muted" style={{ marginBottom: 10 }}>Toàn bộ dữ liệu trong {files.length} file dưới đây sẽ ghi vào <b style={{ color: 'var(--accent)' }}>{valid ? label(month) : '…'}</b>.</div>
      <div className="tbl-wrap framed">
        <table style={{ fontSize: 13 }}>
          <thead><tr><th>File</th><th className="c">Số NV</th><th>Tháng trong file</th></tr></thead>
          <tbody>
            {files.map((f, i) => (
              <tr key={i}>
                <td className="small">{f.name}</td>
                <td className="c">{f.count}</td>
                <td className="small muted">{f.month ? label(f.month) : '(không rõ)'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {diff && <div className="small" style={{ color: 'var(--warn)', marginTop: 8 }}>⚠ Có file ghi tháng khác trong dữ liệu — vẫn sẽ ghi vào {label(month)} theo lựa chọn của bạn.</div>}
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <button className="btn" disabled={!valid} onClick={() => onConfirm(month)}>Cập nhật {label(month)}</button>
      </div>
    </div>
  );
}

export default function AttendanceTab({ me }) {
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [cfg, setCfg] = useState({ types: [], employees: [], me: {} });
  const [userId, setUserId] = useState('');
  // Mặc định: LUÔN tháng hiện tại.
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ days: [], stats: {} });
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [holidays, setHol] = useState(0);
  const [importing, setImporting] = useState(false);
  const [empmap, setEmpmap] = useState({ mappings: [], roster: [] });
  const [showMap, setShowMap] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [savingCode, setSavingCode] = useState(''); // mã đang lưu ánh xạ (hiện spinner)
  const [savedCode, setSavedCode] = useState(''); // mã vừa lưu xong (hiện ✓ Đã lưu)
  const [unlockedCodes, setUnlockedCodes] = useState(() => new Set()); // mã đã gán được mở khoá tạm để sửa
  const [lock, setLock] = useState({ locked: false, lockedBy: '', canUnlock: false });

  const canManage = cfg.me.canManage;
  // Duyệt đơn: role TP, GĐ/CV, HOẶC trưởng phòng ban (kể cả role NV như TP kế toán/DVKH).
  const canApprove = me.role === 'TP' || me.role === 'GD' || me.role === 'CV' || (me.deptHeadOf || []).length > 0;
  const viewingSelf = !userId || String(userId) === String(me.id || me._id);
  const canPropose = (me.role === 'TP' || me.role === 'NV') && viewingSelf && !lock.locked;

  useEffect(() => { attendanceConfig().then((c) => { setCfg(c); if (c.employees.length && !c.me.canAttendance) setUserId(c.employees[0]._id); }).catch(() => {}); }, []);
  const loadMonth = () => { setLoading(true); attendanceMonth(userId, month).then(setData).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(loadMonth, [userId, month]); // eslint-disable-line
  useEffect(() => { getHolidays(month).then((d) => setHol(d.holidays)).catch(() => {}); }, [month]);
  const loadMap = () => { if (canManage) return getEmpMap().then(setEmpmap).catch(() => {}); };
  useEffect(loadMap, [canManage]); // eslint-disable-line
  const loadLock = () => { attLockStatus(month).then(setLock).catch(() => {}); };
  useEffect(loadLock, [month]); // eslint-disable-line
  const locked = lock.locked;
  async function toggleLock() {
    if (!locked) {
      if (!window.confirm(`CHỐT bảng chấm công tháng ${month}?\n\nSau khi chốt: KHÔNG import lại, KHÔNG dọn, KHÔNG sửa/xác nhận ngày, KHÔNG tạo đơn cho tháng này. Chỉ Giám đốc mới mở khoá lại được.`)) return;
      try { await lockAttMonth(month); toast(`Đã CHỐT chấm công tháng ${month}`); loadLock(); loadMonth(); setRefreshKey((k) => k + 1); }
      catch (e) { toast(e.response?.data?.message || 'Lỗi chốt', 'danger'); }
    } else {
      if (!window.confirm(`Mở khoá tháng ${month} để sửa lại?`)) return;
      try { await unlockAttMonth(month); toast(`Đã mở khoá tháng ${month}`); loadLock(); }
      catch (e) { toast(e.response?.data?.message || 'Lỗi mở khoá', 'danger'); }
    }
  }
  async function assignMap(excelCode, userId, force) {
    setSavingCode(excelCode);
    setSavedCode('');
    try {
      const r = await setEmpMap(excelCode, userId, force);
      await loadMap();
      loadMonth();
      setRefreshKey((k) => k + 1);
      setUnlockedCodes((s) => { const n = new Set(s); n.delete(excelCode); return n; }); // gán xong -> khoá lại
      toast(userId
        ? `Đã gán mã ${excelCode}${r?.applied ? ` · áp ${r.applied} ngày công` : ''}`
        : `Đã gỡ gán mã ${excelCode}${r?.purged ? ` · xoá ${r.purged} ngày` : ''}`);
      setSavedCode(excelCode);
      setTimeout(() => setSavedCode((c) => (c === excelCode ? '' : c)), 2500);
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi lưu ánh xạ', 'danger');
    } finally {
      setSavingCode('');
    }
  }

  // DỌN dữ liệu tháng trước khi up lại file — xử lý triệt để trùng mã (xoá ngày chưa chốt của cả tháng,
  // giữ ánh xạ + ngày đã duyệt). Phải import lại ĐẦY ĐỦ các file của tháng sau khi dọn.
  async function purgeMonth() {
    const ok = window.confirm(`Dọn toàn bộ chấm công CHƯA chốt của tháng ${month}?\n\nDùng khi bị TRÙNG MÃ / dữ liệu sai. Ngày đã duyệt đơn / kế toán đã xác nhận vẫn được giữ.\nSau khi dọn phải IMPORT LẠI ĐẦY ĐỦ các file của tháng.`);
    if (!ok) return;
    setImporting(true);
    try {
      const r = await purgeAttendanceMonth(month);
      toast(`Đã dọn ${r.deleted} ngày của tháng ${month}${r.kept ? ` · giữ ${r.kept} ngày đã chốt` : ''} — hãy import lại file`);
      loadMonth(); loadMap(); setRefreshKey((k) => k + 1);
    } catch (e) { toast(e.response?.data?.message || 'Lỗi dọn dữ liệu', 'danger'); }
    finally { setImporting(false); }
  }

  async function saveHolidays() {
    try { await setHolidays(month, holidays); toast('Đã lưu số ngày nghỉ lễ'); loadMonth(); }
    catch (e) { toast(e.response?.data?.message || 'Lỗi lưu nghỉ lễ', 'danger'); }
  }
  // Chọn file -> đọc trước để lấy tháng -> mở modal cho kế toán XÁC NHẬN tháng rồi mới ghi.
  async function onFilesSelected(files) {
    const list = files ? Array.from(files) : [];
    if (!list.length) return;
    setImporting(true);
    try {
      const parsed = [];
      for (const file of list) {
        const buf = await file.arrayBuffer();
        const p = await parseAttendanceXlsx(buf);
        parsed.push({ name: file.name, month: p.month, count: p.employees.length, employees: p.employees });
      }
      openModal(<ImportConfirm files={parsed} defaultMonth={month} onClose={closeModal} onConfirm={(chosen) => doImport(parsed, chosen)} />);
    } catch (e) { toast(e.response?.data?.message || 'Lỗi đọc file', 'danger'); }
    finally { setImporting(false); }
  }
  // Đổi YYYY-MM của mọi ngày sang tháng đã chọn (giữ ngày trong tháng) + tính lại thứ.
  function remapMonth(days, mth) {
    return (days || []).filter((d) => /-(\d{2})$/.test(d.date || '')).map((d) => {
      const dd = d.date.slice(-2);
      const date = `${mth}-${dd}`;
      return { ...d, date, weekday: new Date(`${date}T00:00:00`).getDay() };
    });
  }
  async function doImport(parsed, chosen) {
    if (!/^\d{4}-\d{2}$/.test(chosen || '')) return;
    closeModal();
    setImporting(true);
    try {
      let codes = 0, okFiles = 0;
      for (const p of parsed) {
        if (!p.employees.length) continue;
        const employees = p.employees.map((e) => ({ ...e, days: remapMonth(e.days, chosen) }));
        const r = await importAttendance({ month: chosen, employees });
        codes += (r.scannedCodes || 0); okFiles += 1;
      }
      if (!okFiles) { toast('Không đọc được dữ liệu hợp lệ trong file', 'danger'); return; }
      // GÁN TAY 100%: import chỉ nạp mã vào bảng ánh xạ, chưa ghi công cho ai — kế toán gán từng mã.
      toast(`Đã nạp ${codes} mã từ ${okFiles} file vào bảng ánh xạ — hãy GÁN nhân viên cho từng mã để lên bảng`);
      setMonth(chosen);
      loadMonth(); loadMap(); setRefreshKey((k) => k + 1); setShowMap(true);
    } catch (e) { toast(e.response?.data?.message || 'Lỗi import file', 'danger'); }
    finally { setImporting(false); }
  }
  const loadInbox = () => { if (canApprove) attInbox().then((d) => setInbox(d.items)).catch(() => {}); };
  useEffect(loadInbox, []); // eslint-disable-line

  const dayMap = {};
  data.days.forEach((d) => (dayMap[Number(d.date.slice(-2))] = d));
  const first = data.days[0];
  const lead = first ? colOf(first.weekday) : 0;
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  data.days.forEach((d) => cells.push(d));

  async function submitProposal(date, type, reason) {
    try { await createAttProposal({ date, type, reason }); toast('Đã gửi đề xuất, chờ duyệt'); closeModal(); loadMonth(); }
    catch (e) { toast(e.response?.data?.message || 'Lỗi gửi đề xuất', 'danger'); }
  }
  function clickDay(d) {
    if (!canPropose || !d) return;
    openModal(<ProposalModal types={cfg.types} date={d.date} onClose={closeModal} onSubmit={(t, r) => submitProposal(d.date, t, r)} />);
  }
  async function decide(id, action) {
    try { await decideAttProposal(id, action); toast(action === 'approve' ? 'Đã duyệt đơn' : 'Đã từ chối'); loadInbox(); loadMonth(); }
    catch (e) { toast(e.response?.data?.message || 'Lỗi xử lý đơn', 'danger'); }
  }

  const s = data.stats || {};
  return (
    <>
      <div className="card">
        <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {(!canManage && cfg.employees.length > 1) && (
              <select value={userId} onChange={(e) => setUserId(e.target.value)} style={{ width: 'auto' }}>
                {cfg.me.canAttendance && <option value="">Tôi ({me.name || 'bản thân'})</option>}
                {cfg.employees.filter((u) => String(u._id) !== String(me.id || me._id)).map((u) => (
                  <option key={u._id} value={u._id}>{u.name}{u.role === 'TP' ? ' (TP)' : ''}</option>
                ))}
              </select>
            )}
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }} />
            {canManage && (
              <span className="row" style={{ gap: 4, alignItems: 'center' }} title="Số ngày nghỉ lễ trong tháng">
                <span className="small muted">Nghỉ lễ</span>
                <input type="number" min="0" max="31" value={holidays} onChange={(e) => setHol(Number(e.target.value))} style={{ width: 56 }} />
                <button className="btn sm ghost" onClick={saveHolidays}>Lưu</button>
              </span>
            )}
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {cfg.me?.canAttendance && (
              <button className="btn sm" onClick={() => openModal(<FaceAttendanceModal onClose={closeModal} toast={toast} />)} title="Chấm công vào/ra bằng khuôn mặt">
                📷 Chấm công online
              </button>
            )}
            {(me.role === 'GD' || me.role === 'CV') && (
              <button className="btn sm ghost" onClick={() => openModal(<OnlineAttAdminModal onClose={closeModal} toast={toast} />)} title="Cấu hình chấm công online + nhật ký">
                ⚙️ Cấu hình online
              </button>
            )}
            {canPropose && <span className="small muted">Bấm vào 1 ngày để đề xuất / giải trình</span>}
            {locked && (
              <span className="small" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, fontWeight: 600, color: '#fff', background: 'var(--danger)' }}
                title={`Đã chốt${lock.lockedBy ? ` bởi ${lock.lockedBy}` : ''} — không thể sửa`}>
                🔒 Đã chốt {lock.lockedBy ? `· ${lock.lockedBy}` : ''}
              </span>
            )}
            {canManage && !locked && (
              <button className="btn sm ghost danger" disabled={importing} onClick={purgeMonth}
                title="Xoá chấm công CHƯA chốt của tháng để up lại sạch — dùng khi bị trùng mã / dữ liệu sai (giữ ngày đã duyệt + ánh xạ)">
                🧹 Dọn tháng
              </button>
            )}
            {canManage && !locked && (
              <label className="btn sm" style={{ cursor: 'pointer' }} title="Tự nhận diện VP HN & HCM · chọn được NHIỀU file cùng lúc; xác nhận tháng trước khi ghi">
                {importing ? 'Đang import…' : '⭱ Import Excel chấm công'}
                <input type="file" accept=".xlsx,.xls" multiple style={{ display: 'none' }} disabled={importing} onChange={(e) => { onFilesSelected(e.target.files); e.target.value = ''; }} />
              </label>
            )}
            {canManage && !locked && (
              <button className="btn sm" style={{ background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' }} onClick={toggleLock}
                title="Chốt bảng chấm công cả tháng — sau khi chốt không thể import/dọn/sửa">
                🔒 Chốt bảng chấm công
              </button>
            )}
            {locked && lock.canUnlock && (
              <button className="btn sm ghost" onClick={toggleLock} title="Chỉ Giám đốc — mở khoá để sửa lại">🔓 Mở khoá</button>
            )}
          </div>
        </div>

        {!canManage && (<>
        <div className="grid kpi-grid" style={{ marginBottom: 14 }}>
          <div className="stat"><div className="label">Tổng ngày công</div><div className="val">{s.tongNgayCong ?? '—'}</div></div>
          <div className="stat"><div className="label">Ngày đi làm</div><div className="val" style={{ color: 'var(--ok)' }}>{s.diLam ?? '—'}</div></div>
          <div className="stat"><div className="label">Ngày nghỉ</div><div className="val" style={{ color: 'var(--danger)' }}>{s.nghi ?? '—'}</div></div>
          <div className="stat"><div className="label">Lần bị phạt</div><div className="val" style={{ color: 'var(--warn)' }}>{s.biPhat ?? '—'}</div></div>
          <div className="stat"><div className="label">🏖 Phép còn lại</div><div className="val" style={{ color: 'var(--accent)' }}>{data.leaveBalance ?? 0}</div></div>
        </div>

        {loading ? <div className="empty">Đang tải…</div> : !data.days.length ? (
          <div className="empty">Chưa có dữ liệu chấm công tháng này.</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5, marginBottom: 5 }}>
              {WD.map((w) => <div key={w} style={{ textAlign: 'center', fontSize: 11.5, fontWeight: 600, color: 'var(--muted2)' }}>{w}</div>)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5 }}>
              {cells.map((d, i) => {
                if (!d) return <div key={`b${i}`} style={{ minHeight: 58 }} />;
                const st = ST[d.status] || ST.off;
                return (
                  <div key={d.date} onClick={() => clickDay(d)}
                    style={{ minHeight: 58, borderRadius: 8, border: `1px solid ${st.bd}`, background: d.status === 'half' ? HALF_BG : st.bg, padding: '5px 6px', position: 'relative', cursor: canPropose ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: st.tx }}>{Number(d.date.slice(-2))}</div>
                    <div style={{ fontSize: 10.5, lineHeight: 1.25, color: st.tx }}>{labelOf(d)}</div>
                    {/* Dữ liệu theo IP (mốc ở cty + vắng): CHỈ kế toán/GĐ thấy; nhân viên chỉ thấy dữ liệu chấm công. */}
                    {canManage && d.awayHours > 0 && <div title={`Vắng mặt ở công ty (Wifi/IP): ${(d.awayLog || []).join(', ')}`} style={{ fontSize: 9.5, color: 'var(--warn)', fontWeight: 700 }}>⚠ vắng {d.awayHours}h</div>}
                    {canManage && d.firstSeenCompany && <div title="Mốc ping ở công ty (tham khảo, không thay check-in/out)" style={{ fontSize: 9, color: 'var(--muted2)' }}>🏢 {d.firstSeenCompany}–{d.lastSeenCompany || d.firstSeenCompany}</div>}
                    {d.adminAdjusted ? <span title="Kế toán đã xác nhận" style={{ position: 'absolute', top: 3, right: 6, fontSize: 13, fontWeight: 800, color: 'var(--ok)', lineHeight: 1 }}>✓</span>
                      : d.approved ? <span title="Có đơn được duyệt" style={{ position: 'absolute', top: 5, right: 6, width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)' }} />
                      : d.proposal && d.proposal.status === 'pending' ? <span title="Đã gửi đề xuất, chờ duyệt" style={{ position: 'absolute', top: 5, right: 6, width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)' }} /> : null}
                  </div>
                );
              })}
            </div>
            <div className="row" style={{ gap: 14, flexWrap: 'wrap', marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>
              <span><i style={{ color: 'var(--ok)' }}>●</i> Đi làm đúng giờ</span>
              <span><i style={{ color: 'var(--warn-fill)' }}>●</i> Đi muộn / thiếu 1 lần chấm</span>
              <span><i style={{ display: 'inline-block', width: 11, height: 11, borderRadius: 3, verticalAlign: '-1px', background: HALF_BG, border: '1px solid color-mix(in srgb, var(--ok) 40%, transparent)' }} /> Nửa công (chỉ làm 1 buổi)</span>
              <span><i style={{ color: 'var(--danger)' }}>●</i> Nghỉ làm</span>
              <span><i style={{ color: 'var(--muted2)' }}>●</i> CN / T7 nghỉ ĐK (không tính)</span>
              <span><i style={{ color: 'var(--danger)' }}>●</i> chấm đỏ góc = đã gửi đề xuất / được duyệt</span>
            </div>
          </>
        )}
        </>)}
      </div>

      {canManage && <AttendanceMatrix month={month} refreshKey={refreshKey} toast={toast} openModal={openModal} closeModal={closeModal} locked={locked} />}

      {canManage && (
        <div className="card">
          <div className="row between" style={{ cursor: 'pointer' }} onClick={() => setShowMap((v) => !v)}>
            <h3 style={{ margin: 0 }}>🔗 Ánh xạ mã chấm công ↔ nhân viên ({empmap.mappings.length})
              {(() => { const n = empmap.mappings.filter((m) => !(m.user?._id || m.user)).length; return n ? <span className="small" style={{ color: 'var(--danger)', marginLeft: 8, fontWeight: 600 }}>· {n} chưa gán</span> : null; })()}
            </h3>
            <span className="small muted">{showMap ? '▲ Thu gọn' : '▼ Mở'}</span>
          </div>
          {showMap && (
            <>
              <div className="small muted" style={{ margin: '6px 0 10px' }}>
                Mã + tên quét từ Excel. <b>Hệ thống KHÔNG tự gán</b> — chọn nhân viên cho từng mã thì chấm công của mã đó mới lên bảng (tránh nhầm do trùng mã). Mã đã gán sẽ <b>KHOÁ</b> (🔒) để khỏi đổi/ghi đè nhầm — bấm 🔒 mở khoá mới đổi/gỡ được người; <b>↻</b> = áp lại cho đúng người đó sau khi import lại.
              </div>
              {!empmap.mappings.length ? <div className="empty">Chưa có mã nào — import Excel để nạp danh sách.</div> : (
                <div className="tbl-wrap framed">
                  <table style={{ fontSize: 13 }}>
                    <thead><tr><th style={{ width: 110 }}>Mã (Excel)</th><th>Tên (Excel)</th><th style={{ width: 300 }}>Nhân viên FunnyTask</th></tr></thead>
                    <tbody>
                      {[...empmap.mappings].sort((a, b) => (!(a.user?._id || a.user) ? 0 : 1) - (!(b.user?._id || b.user) ? 0 : 1) || String(a.excelCode).localeCompare(String(b.excelCode))).map((m) => (
                        <tr key={m.excelCode} style={{ background: !(m.user?._id || m.user) ? 'color-mix(in srgb, var(--danger) 6%, transparent)' : undefined }}>
                          <td><code style={{ color: 'var(--warn)' }}>{m.excelCode}</code></td>
                          <td>{m.excelName || '—'}</td>
                          <td>
                            {(() => {
                              const assigned = !!(m.user?._id || m.user);
                              const rowLocked = assigned && !unlockedCodes.has(m.excelCode); // đã gán + chưa mở khoá
                              return (
                            <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                              <select value={m.user?._id || m.user || ''} disabled={locked || rowLocked || savingCode === m.excelCode} onChange={(e) => assignMap(m.excelCode, e.target.value, true)} style={{ flex: 1 }}>
                                <option value="">— Chưa gán —</option>
                                {empmap.roster.map((u) => (
                                  <option key={u._id} value={u._id}>{u.name}{u.employeeCode ? ` (${u.employeeCode})` : ''}{u.role === 'TP' ? ' · TP' : ''}</option>
                                ))}
                              </select>
                              {savingCode === m.excelCode ? (
                                <span className="small muted" style={{ whiteSpace: 'nowrap' }}>Đang lưu…</span>
                              ) : savedCode === m.excelCode ? (
                                <span className="small" style={{ color: 'var(--ok, #16a34a)', whiteSpace: 'nowrap' }}>✓ Đã lưu</span>
                              ) : null}
                              {assigned && !locked && savingCode !== m.excelCode && (rowLocked
                                ? <button className="btn sm ghost" title="Đã khoá — bấm để mở khoá rồi mới đổi/gỡ được người" onClick={() => setUnlockedCodes((s) => new Set(s).add(m.excelCode))}>🔒</button>
                                : <button className="btn sm ghost" title="Đang mở khoá — bấm để khoá lại" onClick={() => setUnlockedCodes((s) => { const n = new Set(s); n.delete(m.excelCode); return n; })}>🔓</button>
                              )}
                              {assigned && !locked && !rowLocked && savingCode !== m.excelCode && (
                                <button className="btn sm ghost" title="Áp lại chấm công vừa import cho người này (dùng sau khi import lại)" onClick={() => assignMap(m.excelCode, m.user?._id || m.user)}>↻</button>
                              )}
                            </div>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {canApprove && (
        <div className="card">
          <h3>📥 Đơn chờ duyệt ({inbox.length})</h3>
          {!inbox.length ? <div className="empty">Không có đơn nào chờ bạn duyệt.</div> : inbox.map((p) => (
            <div key={p._id} className="row between" style={{ padding: '8px 0', borderTop: '1px solid var(--line)', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <b>{p.user?.name}</b> <span className="small muted">{p.user?.role === 'TP' ? '(TP)' : ''}</span> · {p.date.split('-').reverse().join('/')}
                <div className="small"><b style={{ color: 'var(--accent)' }}>{p.typeLabel}</b>{p.reason ? ` — ${p.reason}` : ''}</div>
              </div>
              <div className="row" style={{ gap: 6 }}>
                <button className="btn sm" onClick={() => decide(p._id, 'approve')}>✓ Duyệt</button>
                <button className="btn sm ghost danger" onClick={() => decide(p._id, 'reject')}>✕ Từ chối</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
